import { IntlShape } from "react-intl";

import { forceLocalDate } from "@futrli/backend-messages/this-number-explained/messages";

export interface ColumnInfo {
  heading?: HeadingInfo;
  sub_heading?: SubHeadingInfo;
}

export interface HeadingInfo {
  id: string;
  start_date?: string;     // ISO date string representing the first day of the period (e.g. 2020-12-01)
  end_date?: string;       // ISO date string representing the last day of the period (e.g. 2020-12-31)
  financial_year?: number; // The financial year the period falls in
  dataset_name?: string;   // Dataset name (passed to the backend as part of a dataset/comparison dataset request)
}

export interface SubHeadingInfo {
  simple?: HeadingInfo;                  // A simple sub-heading, e.g. for a Total, Average, etc.
  comparison?: ComparisonSubHeadingInfo; // A comparison between two columns, e.g. May 21 vs May 22 or Mar 23 vs Budget
}

export interface ComparisonSubHeadingInfo {
  primary: HeadingInfo;
  secondary: HeadingInfo;
}

const defaultYear = 1900;

type TranslationFunction = (info: HeadingInfo, intl: IntlShape) => string;

/**
 * Reporting headings defined as translatable messages. For date format syntax refer to:
 * - https://unicode-org.github.io/icu/userguide/format_parse/datetime/#date-field-symbol-table
 *
 * NOTE: Message parameters passed in are defined in the backend (see https://github.com/FUTRLI/predict-data-service).
 *       Do not alter the values fields (or their types) in a FormattedMessage without also altering the backend code
 *       to match.
 *
 * NOTE: Message IDs defined here should start with "pds.reporting.heading."
 *
 * NOTE: DO NOT REMOVE MESSAGES OR ALTER FormattedMessage IDS WITHOUT VERIFYING THAT THEY ARE NO LONGER USED BY
 *       PREDICT DATA SERVICE (see https://github.com/FUTRLI/predict-data-service)
 */
const headings: Record<string, TranslationFunction> = {
  'pds.reporting.heading.empty': (_info: HeadingInfo, _intl: IntlShape): string => {
    return '';
  },
  'pds.reporting.heading.financial_year': (info: HeadingInfo, intl: IntlShape): string => {
    const data = getDefaultHeadingData(info);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.financial_year',
        description: 'Financial year, e.g. 2006',
        defaultMessage: '{financial_year}',
      },
      {
        financial_year: intl.formatDate(data.financial_year, {year: 'numeric'}),
      },
    );
  },
  'pds.reporting.heading.financial_quarter': (info: HeadingInfo, intl: IntlShape): string => {
    const data = getDefaultHeadingData(info);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.financial_quarter',
        description: 'Financial quarter, e.g. Apr-Jun 06',
        defaultMessage: '{start_date}-{end_date}',
      },
      {
        start_date: intl.formatDate(data.start_date, {month: 'short'}),
        end_date: intl.formatDate(data.end_date, {month: 'short', year: '2-digit'}),
      },
    );
  },
  'pds.reporting.heading.month': (info: HeadingInfo, intl: IntlShape): string => {
    const data = getDefaultHeadingData(info);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.month',
        description: 'Month, e.g. Jun 06',
        defaultMessage: '{start_date}',
      },
      {
        start_date: intl.formatDate(data.start_date, {month: 'short', year: '2-digit'}),
      },
    );
  },
  'pds.reporting.heading.week': (info: HeadingInfo, intl: IntlShape): string => {
    const data = getDefaultHeadingData(info);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.week',
        description: 'Week starting on day, e.g. Mon, 02 Jan 06',
        defaultMessage: '{start_date}',
      },
      {
        start_date: intl.formatDate(data.start_date, {weekday: 'short', day:'2-digit', month: 'short', year: '2-digit'}),
      },
    );
  },
  'pds.reporting.heading.day': (info: HeadingInfo, intl: IntlShape): string => {
    const data = getDefaultHeadingData(info);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.day',
        description: 'Day, e.g. 02 Jan 06',
        defaultMessage: '{start_date}',
      },
      {
        start_date: intl.formatDate(data.start_date, {day:'2-digit', month: 'short', year: '2-digit'}),
      },
    );
  },
  'pds.reporting.heading.to_report_end.financial_year': (info: HeadingInfo, intl: IntlShape): string => {
    const data = getDefaultHeadingData(info);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.to_report_end.financial_year',
        description: 'Financial year to report end heading where last period is a financial year, e.g. 2023 (to 2023)',
        defaultMessage: '{financial_year} (to {financial_year})',
      },
      {
        financial_year: intl.formatDate(data.financial_year, {year: 'numeric'}),
      },
    );
  },
  'pds.reporting.heading.to_report_end.financial_quarter': (info: HeadingInfo, intl: IntlShape): string => {
    const data = getDefaultHeadingData(info);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.to_report_end.financial_quarter',
        description: 'Financial year to report end heading where last period is a financial quarter, e.g. 2023 (to Apr-Jun 23)',
        defaultMessage: '{financial_year} (to {start_date}-{end_date})',
      },
      {
        financial_year: intl.formatDate(data.financial_year, {year: 'numeric'}),
        start_date: intl.formatDate(data.start_date, {month: 'short'}),
        end_date: intl.formatDate(data.end_date, {month: 'short', year: '2-digit'}),
      },
    );
  },
  'pds.reporting.heading.to_report_end.month': (info: HeadingInfo, intl: IntlShape): string => {
    const data = getDefaultHeadingData(info);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.to_report_end.month',
        description: 'Financial year to report end heading where last period is a month, e.g. 2023 (to Oct)',
        defaultMessage: '{financial_year} (to {end_date})',
      },
      {
        financial_year: intl.formatDate(data.financial_year, {year: 'numeric'}),
        end_date: intl.formatDate(data.end_date, {month: 'short'}),
      },
    );
  },
  'pds.reporting.heading.to_report_end.week': (info: HeadingInfo, intl: IntlShape): string => {
    const data = getDefaultHeadingData(info);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.to_report_end.week',
        description: 'Financial year to report end heading where last period is a week, e.g. 2022 (to Sun 13 Nov 22)',
        defaultMessage: '{financial_year} (to {end_date})',
      },
      {
        financial_year: intl.formatDate(data.financial_year, {year: 'numeric'}),
        end_date: intl.formatDate(data.end_date, {weekday: 'short', day:'2-digit', month: 'short', year: '2-digit'}),
      },
    );
  },
  'pds.reporting.heading.to_report_end.day': (info: HeadingInfo, intl: IntlShape): string => {
    const data = getDefaultHeadingData(info);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.to_report_end.day',
        description: 'Financial year to report end heading where last period is a day, e.g. 2023 (to 09 Oct 23)',
        defaultMessage: '{financial_year} (to {end_date})',
      },
      {
        financial_year: intl.formatDate(data.financial_year, {year: 'numeric'}),
        end_date: intl.formatDate(data.end_date, {day:'2-digit', month: 'short', year: '2-digit'}),
      },
    );
  },
  'pds.reporting.heading.this_month_vs_last_month': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.this_month_vs_last_month',
        description: 'Variance column: this month vs last month',
        defaultMessage: 'TM vs LM',
      },
    )
  },
  'pds.reporting.heading.this_month_vs_last_month_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.this_month_vs_last_month_percent',
        description: 'Variance column: this month vs last month (as a percentage)',
        defaultMessage: 'TM vs LM %',
      },
    )
  },
  'pds.reporting.heading.last_month': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.last_month',
        description: 'Variance column: last month',
        defaultMessage: 'LM',
      },
    )
  },
  'pds.reporting.heading.month_to_date': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.month_to_date',
        description: 'Variance column: month to date',
        defaultMessage: 'MTD',
      },
    )
  },
  'pds.reporting.heading.month_to_date_vs_last_month': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.month_to_date_vs_last_month',
        description: 'Variance column: month to date vs last month',
        defaultMessage: 'MTD vs LM',
      },
    )
  },
  'pds.reporting.heading.month_to_date_vs_last_month_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.month_to_date_vs_last_month_percent',
        description: 'Variance column: month to date vs last month (as a percentage)',
        defaultMessage: 'MTD vs LM %',
      },
    )
  },
  'pds.reporting.heading.last_month_to_date': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.last_month_to_date',
        description: 'Variance column: last month to date',
        defaultMessage: 'LMTD',
      },
    )
  },
  'pds.reporting.heading.month_to_date_vs_last_month_to_date': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.month_to_date_vs_last_month_to_date',
        description: 'Variance column: month to date vs last month to date',
        defaultMessage: 'MTD vs LMTD',
      },
    )
  },
  'pds.reporting.heading.month_to_date_vs_last_month_to_date_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.month_to_date_vs_last_month_to_date_percent',
        description: 'Variance column: month to date vs last month to date (as a percentage)',
        defaultMessage: 'MTD vs LMTD %',
      },
    )
  },
  'pds.reporting.heading.this_quarter_vs_last_quarter': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.this_quarter_vs_last_quarter',
        description: 'Variance column: this quarter vs last quarter',
        defaultMessage: 'TQ vs LQ',
      },
    )
  },
  'pds.reporting.heading.this_quarter_vs_last_quarter_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.this_quarter_vs_last_quarter_percent',
        description: 'Variance column: this quarter vs last quarter (as a percentage)',
        defaultMessage: 'TQ vs LQ %',
      },
    )
  },
  'pds.reporting.heading.last_quarter': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.last_quarter',
        description: 'Variance column: last quarter',
        defaultMessage: 'LQ',
      },
    )
  },
  'pds.reporting.heading.quarter_to_date': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.quarter_to_date',
        description: 'Variance column: quarter to date',
        defaultMessage: 'QTD',
      },
    )
  },
  'pds.reporting.heading.quarter_to_date_vs_last_quarter': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.quarter_to_date_vs_last_quarter',
        description: 'Variance column: quarter to date vs last quarter',
        defaultMessage: 'QTD vs LQ',
      },
    )
  },
  'pds.reporting.heading.quarter_to_date_vs_last_quarter_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.quarter_to_date_vs_last_quarter_percent',
        description: 'Variance column: quarter to date vs last quarter (as a percentage)',
        defaultMessage: 'QTD vs LQ %',
      },
    )
  },
  'pds.reporting.heading.last_quarter_to_date': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.last_quarter_to_date',
        description: 'Variance column: last quarter to date',
        defaultMessage: 'LQTD',
      },
    )
  },
  'pds.reporting.heading.quarter_to_date_vs_last_quarter_to_date': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.quarter_to_date_vs_last_quarter_to_date',
        description: 'Variance column: quarter to date vs last quarter to date',
        defaultMessage: 'QTD vs LQTD',
      },
    )
  },
  'pds.reporting.heading.quarter_to_date_vs_last_quarter_to_date_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.quarter_to_date_vs_last_quarter_to_date_percent',
        description: 'Variance column: quarter to date vs last quarter to date (as a percentage)',
        defaultMessage: 'QTD vs LQTD %',
      },
    )
  },
  'pds.reporting.heading.fy_vs_last_fy': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.fy_vs_last_fy',
        description: 'Variance column: current financial year vs last financial year',
        defaultMessage: 'FY vs LFY',
      },
    )
  },
  'pds.reporting.heading.fy_vs_last_fy_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.fy_vs_last_fy_percent',
        description: 'Variance column: current financial year vs last financial year (as a percentage)',
        defaultMessage: 'FY vs LFY %',
      },
    )
  },
  'pds.reporting.heading.year_to_date': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.year_to_date',
        description: 'Variance column: current financial year to date',
        defaultMessage: 'YTD',
      },
    )
  },
  'pds.reporting.heading.fy_to_date': (_: HeadingInfo, intl: IntlShape): string => {
    /*
     * NOTE: Duplicate of pds.reporting.heading.year_to_date with slightly different text,
     *       kept to preserve current behaviour.
     */
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.fy_to_date',
        description: 'Variance column: current financial year to date',
        defaultMessage: 'FYTD',
      },
    )
  },
  'pds.reporting.heading.year_to_date_vs_last_fy': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.year_to_date_vs_last_fy',
        description: 'Variance column: current financial year to date vs last financial year',
        defaultMessage: 'YTD vs LFY',
      },
    )
  },
  'pds.reporting.heading.year_to_date_vs_last_fy_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.year_to_date_vs_last_fy_percent',
        description: 'Variance column: current financial year to date vs last financial year (as a percentage)',
        defaultMessage: 'YTD vs LFY %',
      },
    )
  },
  'pds.reporting.heading.last_year_to_date': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.last_year_to_date',
        description: 'Variance column: previous financial year to date',
        defaultMessage: 'LYTD',
      },
    )
  },
  'pds.reporting.heading.fy_to_date_vs_last_fy_to_date': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.fy_to_date_vs_last_fy_to_date',
        description: 'Variance column: current financial year to date vs last financial year to date',
        defaultMessage: 'YTD vs LYTD',
      },
    )
  },
  'pds.reporting.heading.fy_to_date_vs_last_fy_to_date_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.fy_to_date_vs_last_fy_to_date_percent',
        description: 'Variance column: current financial year to date vs last financial year to date (as a percentage)',
        defaultMessage: 'YTD vs LYTD %',
      },
    )
  },
  'pds.reporting.heading.this_month_vs_this_month_last_year': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.this_month_vs_this_month_last_year',
        description: 'Variance column: current month vs the same month in the previous year',
        defaultMessage: 'TM vs TMLY',
      },
    )
  },
  'pds.reporting.heading.this_month_vs_this_month_last_year_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.this_month_vs_this_month_last_year_percent',
        description: 'Variance column: current month vs the same month in the previous year (as a percentage)',
        defaultMessage: 'TM vs TMLY %',
      },
    )
  },
  'pds.reporting.heading.this_quarter_vs_this_quarter_last_year': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.this_quarter_vs_this_quarter_last_year',
        description: 'Variance column: current quarter vs the same quarter in the previous year',
        defaultMessage: 'TQ vs TQLY',
      },
    )
  },
  'pds.reporting.heading.this_quarter_vs_this_quarter_last_year_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.this_quarter_vs_this_quarter_last_year_percent',
        description: 'Variance column: current quarter vs the same quarter in the previous year (as a percentage)',
        defaultMessage: 'TQ vs TQLY %',
      },
    )
  },
  'pds.reporting.heading.percent_of_revenue': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.percent_of_revenue',
        description: 'Variance column: the previous column value expressed as a percent of revenue',
        defaultMessage: '% of revenue',
      },
    )
  },
  'pds.reporting.heading.variance': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.variance',
        description: 'Variance column: the difference between the previous two columns',
        defaultMessage: 'Variance',
      },
    )
  },
  'pds.reporting.heading.variance_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.variance_percent',
        description: 'Variance column: the difference between the previous two columns expressed as a percentage',
        defaultMessage: 'Variance %',
      },
    )
  },
  'pds.reporting.heading.start_end': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.start_end',
        description: 'Variance column: the difference between the first and last period',
        defaultMessage: 'Start/End',
      },
    )
  },
  'pds.reporting.heading.start_end_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.start_end_percent',
        description: 'Variance column: the difference between the first and last period expressed as a percentage',
        defaultMessage: 'Start/End %',
      },
    )
  },
  'pds.reporting.heading.total': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.total',
        description: 'Variance column: the total of the previous columns',
        defaultMessage: 'Total',
      },
    )
  },
  'pds.reporting.heading.average': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.average',
        description: 'Variance column: the average (mean) of the previous columns',
        defaultMessage: 'Average',
      },
    )
  },
  'pds.reporting.heading.short_variance': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.short_variance',
        description: 'Variance column: the difference between the previous columns',
        defaultMessage: '+/-',
      },
    )
  },
  'pds.reporting.heading.short_variance_percent': (_: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.short_variance_percent',
        description: 'Variance column: the difference between the previous columns (as a percentage)',
        defaultMessage: '+/- %',
      },
    )
  },
  'pds.reporting.heading.dataset_name': (info: HeadingInfo, intl: IntlShape): string => {
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.dataset_name',
        description: 'User-supplied dataset name (e.g. a forecast name or "Actuals")',
        defaultMessage: '{dataset_name}',
      },
      getDefaultHeadingData(info),
    )
  },
};

/**
 * getDefaultHeadingData takes heading information provided by the reporting/financial-data
 * backend and returns standardised values for substitution into i18n translatable/formatted
 * messages.
 *
 * @param {HeadingInfo} info
 */
export function getDefaultHeadingData(info: HeadingInfo) {
  return {
    start_date: forceLocalDate(info.start_date),
    end_date: forceLocalDate(info.end_date),
    financial_year: forceLocalDate(`${info.financial_year ?? defaultYear}-01-01`),
    dataset_name: info.dataset_name ?? '',
  }
}

/**
 * translateHeading takes heading information provided by the reporting/financial-data
 * backend and returns an i18n translated heading string.
 *
 * @param {HeadingInfo} info
 * @param {IntlShape} intl
 *
 * @returns {string}
 */
export function translateHeading(info: HeadingInfo, intl: IntlShape): string {
  const translator = headings[info.id];
  if (translator) {
    return translator(info, intl);
  }

  return '';
}

/**
 * translateSubHeading takes sub-heading information provided by the reporting/financial-data
 * backend and returns an i18n translated sub heading string.
 *
 * @param {SubHeadingInfo} info
 * @param {IntlShape} intl
 *
 * @returns {string}
 */
export function translateSubHeading(info: SubHeadingInfo, intl: IntlShape): string {
  const simpleSubHeading = info.simple;
  if (simpleSubHeading) {
    return translateHeading(simpleSubHeading, intl);
  }

  const comparisonSubHeading = info.comparison;
  if (comparisonSubHeading) {
    const primary_name = translateHeading(comparisonSubHeading.primary, intl);
    const secondary_name = translateHeading(comparisonSubHeading.secondary, intl);
    return intl.formatMessage(
      {
        id: 'pds.reporting.heading.comparison',
        description: 'Comparison between two items, e.g. "Forecast vs Budget", "Apr 22 vs Apr 21", ...',
        defaultMessage: '{primary_name} vs {secondary_name}',
      },
      {
        primary_name,
        secondary_name,
      },
    )
  }

  return ''; // Unexpected
}
